import React, { useEffect, useId, useRef, useState } from "react";
import type {
  Office,
  OfficeListItem,
  User,
  Config,
  ConfigVersion,
  Reference,
  FileRecord,
  Permission,
} from "../shared/contracts.ts";
import { statusLabels } from "../shared/contracts.ts";
export type PublicIdentity = Config["identity"];
export type Notice = {
  id?: string;
  title?: string;
  text?: string;
  message?: string;
  read?: boolean;
  createdAt?: string;
  at?: string;
};
export type Integration = {
  id: string;
  name: string;
  ready: boolean;
  message: string;
};
let csrf = "";
class RequestError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.code = code;
  }
}
export async function api<T = any>(
  path: string,
  method = "GET",
  body?: any,
  signal?: AbortSignal,
  responseType: "json" | "pdf" = "json",
): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== "GET") headers["X-CSRF-Token"] = csrf;
  if (body !== undefined && !(body instanceof FormData))
    headers["Content-Type"] = "application/json";
  let response: Response;
  try {
    response = await fetch("/api" + path, {
      method,
      headers,
      signal,
      credentials: "same-origin",
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    throw new RequestError(
      "Não foi possível conectar. Seu texto continua nesta tela. Confira a conexão e tente novamente.",
      "NETWORK",
    );
  }
  const checkCancelled = () => {
    if (signal?.aborted)
      throw new DOMException("Consulta cancelada.", "AbortError");
  };
  checkCancelled();
  if (response.ok && responseType === "pdf") {
    if (!response.headers.get("Content-Type")?.includes("application/pdf"))
      throw new RequestError(
        "A prévia não retornou um PDF válido. Tente novamente.",
        "PDF_RESPONSE",
      );
    const data = await response.blob();
    checkCancelled();
    return data as T;
  }
  let data;
  try {
    data = await response.json();
  } catch (error) {
    checkCancelled();
    if ((error as Error)?.name === "AbortError") throw error;
    if (response.ok)
      throw new RequestError(
        "Não foi possível ler a resposta. Tente novamente.",
        "INVALID_RESPONSE",
      );
    data = {};
  }
  checkCancelled();
  if (!response.ok)
    throw new RequestError(
      typeof data.error === "string"
        ? data.error
        : "A operação não foi concluída. Tente novamente.",
      data.code || String(response.status),
    );
  return data;
}
export const apiPDF = (path: string, body: unknown) =>
  api<Blob>(path, "POST", body, undefined, "pdf");
export const date = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
export const money = (cents: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    cents / 100,
  );
export const bytes = (n: number) =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.ceil(n / 1024)} KB`;
export const fileUrl = (id: string) => "/api/files/" + encodeURIComponent(id);
export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  const paths: Record<string, React.ReactNode> = {
    building: (
      <>
        <path d="m3 8 9-5 9 5H3zM3 21h18M5 18V10m5 8V10m4 8V10m5 8V10M3 18h18" />
      </>
    ),
    chart: (
      <>
        <path d="M3 21h18M6 20v-9h4v9m1 0V4h4v16m1 0v-6h4v6" />
      </>
    ),
    document: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6M8 13h8M8 17h6" />
      </>
    ),
    library: (
      <>
        <rect x="3" y="3" width="5" height="18" rx="1" />
        <rect x="10" y="3" width="5" height="18" rx="1" />
        <path d="m17 4 4 16M4 7h3M11 7h3" />
      </>
    ),
    settings: (
      <>
        <path d="M4 7h16M4 17h16" />
        <circle cx="9" cy="7" r="3" />
        <circle cx="16" cy="17" r="3" />
      </>
    ),
    home: (
      <>
        <path d="m3 10 9-7 9 7v11h-6v-7H9v7H3z" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M7 3v4M17 3v4M3 11h18M7 15h3M14 15h3" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    arrow: <path d="m9 5 7 7-7 7" />,
    back: <path d="m15 5-7 7 7 7" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    spark: (
      <>
        <path d="m12 3 3 6 6 3-6 3-3 6-3-6-6-3 6-3zM20 2v4M18 4h4" />
      </>
    ),
    upload: (
      <>
        <path d="M12 16V3m-5 5 5-5 5 5M4 16v5h16v-5" />
      </>
    ),
    eye: (
      <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    alert: (
      <>
        <path d="m12 3 10 18H2zM12 9v5M12 17v.2" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 21v-3a6 6 0 0 1 12 0v3M17 5a3 3 0 0 1 0 6M18 15a5 5 0 0 1 3 5" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12m-5-5 5 5 5-5M4 17v4h16v-4" />
      </>
    ),
    logout: (
      <>
        <path d="M9 3H4v18h5M9 12h12m-5-5 5 5-5 5" />
      </>
    ),
    offline: (
      <>
        <path d="m3 3 18 18M2 8a17 17 0 0 1 4-3M9 4a17 17 0 0 1 13 4M5 12a11 11 0 0 1 4-2M14 10a11 11 0 0 1 5 2M8 16a6 6 0 0 1 5-1M12 20h.01" />
      </>
    ),
    desktop: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </>
    ),
    send: (
      <>
        <path d="m22 2-7 20-4-9-9-4zM22 2 11 13" />
      </>
    ),
    link: (
      <>
        <path d="M10 13a5 5 0 0 0 7 0l4-4a5 5 0 0 0-7-7l-2 2M14 11a5 5 0 0 0-7 0l-4 4a5 5 0 0 0 7 7l2-2" />
      </>
    ),
    save: (
      <>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12l4 4v12a2 2 0 0 1-2 2M7 3v6h10V3M7 21v-8h10v8" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7a9 9 0 1 0 1 8M20 3v5h-5" />
      </>
    ),
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.document}
    </svg>
  );
}
export function Button({
  children,
  kind = "secondary",
  busy = false,
  icon,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  kind?: string;
  busy?: boolean;
  icon?: string;
}) {
  return (
    <button
      {...props}
      className={`button ${kind} ${props.className || ""}`}
      disabled={props.disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? (
        <span className="spinner" />
      ) : icon ? (
        <Icon name={icon} size={18} />
      ) : null}
      {children}
    </button>
  );
}
export function Banner({
  children,
  kind = "info",
}: {
  children: React.ReactNode;
  kind?: string;
}) {
  return (
    <div
      className={"banner " + kind}
      role={kind === "danger" ? "alert" : "status"}
    >
      <Icon
        name={
          kind === "danger" || kind === "warn"
            ? "alert"
            : kind === "success"
              ? "check"
              : "clock"
        }
        size={19}
      />
      <div>{children}</div>
    </div>
  );
}
export function Badge({ status }: { status: Office["status"] }) {
  return (
    <span
      className={
        "badge " +
        (["recoverable_failure", "awaiting_review"].includes(status)
          ? "warn"
          : status === "protocolled"
            ? "success"
            : status === "draft"
              ? "neutral"
              : "info")
      }
    >
      <Icon
        size={14}
        name={
          status === "protocolled"
            ? "check"
            : status === "draft"
              ? "document"
              : "clock"
        }
      />
      {statusLabels[status]}
    </span>
  );
}
export function Empty({
  title,
  children,
  icon = "document",
  action,
}: {
  title: string;
  children: React.ReactNode;
  icon?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={28} />
      </span>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </div>
  );
}
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
// Modals can be nested and can unmount together when the page or session changes.
// The last owner restores scrolling, regardless of cleanup order.
const modalScrollOwners = new Set<symbol>();
let modalPreviousOverflow = "";
function lockModalScroll() {
  const owner = Symbol("modal");
  if (!modalScrollOwners.size) {
    modalPreviousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
  }
  modalScrollOwners.add(owner);
  return () => {
    modalScrollOwners.delete(owner);
    if (!modalScrollOwners.size)
      document.documentElement.style.overflow = modalPreviousOverflow;
  };
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
  mobileFullScreen = false,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
  mobileFullScreen?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    errorRef = useRef<HTMLDivElement>(null),
    [dialogError, setDialogError] = useState("");
  const titleId = useId();
  useEffect(() => {
    const showError = (event: Event) => {
      setDialogError((event as CustomEvent<string>).detail);
    };
    window.addEventListener("operation-error", showError);
    return () => window.removeEventListener("operation-error", showError);
  }, []);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const unlock = lockModalScroll();
    ref.current?.showModal();
    return () => {
      ref.current?.close();
      unlock();
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    if (!mobileFullScreen) return;
    const viewport = window.visualViewport;
    const resize = () => {
      ref.current?.style.setProperty(
        "--modal-available-height",
        `${viewport?.height ?? window.innerHeight}px`,
      );
      ref.current?.style.setProperty(
        "--modal-viewport-top",
        `${viewport?.offsetTop ?? 0}px`,
      );
    };
    resize();
    viewport?.addEventListener("resize", resize);
    viewport?.addEventListener("scroll", resize);
    window.addEventListener("resize", resize);
    return () => {
      viewport?.removeEventListener("resize", resize);
      viewport?.removeEventListener("scroll", resize);
      window.removeEventListener("resize", resize);
    };
  }, [mobileFullScreen]);
  useEffect(() => {
    if (dialogError) {
      ref.current?.scrollTo({ top: 0 });
      errorRef.current?.focus();
    }
  }, [dialogError]);
  useEffect(() => {
    let frame: number | null = null;
    const viewport = window.visualViewport;
    const revealFocusedField = () => {
      frame = null;
      const dialog = ref.current;
      const active = document.activeElement;
      if (
        !dialog?.open ||
        !(active instanceof HTMLElement) ||
        !dialog.contains(active) ||
        !active.matches(
          "input:not([type=hidden]), textarea, select, [contenteditable=true]",
        )
      )
        return;

      const scrollers: HTMLElement[] = [];
      for (
        let element = active.parentElement;
        element;
        element = element.parentElement
      ) {
        if (
          /auto|scroll|overlay/.test(getComputedStyle(element).overflowY) &&
          element.scrollHeight > element.clientHeight
        )
          scrollers.push(element);
        if (element === dialog) break;
      }
      const bounds = () => {
        const box = dialog.getBoundingClientRect();
        let top = Math.max(
          box.top + dialog.clientTop,
          viewport?.offsetTop ?? 0,
        );
        let bottom = Math.min(
          box.top + dialog.clientTop + dialog.clientHeight,
          (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight),
        );
        for (const scroller of scrollers) {
          const area = scroller.getBoundingClientRect();
          top = Math.max(top, area.top + scroller.clientTop);
          bottom = Math.min(
            bottom,
            area.top + scroller.clientTop + scroller.clientHeight,
          );
        }
        const header = dialog.querySelector<HTMLElement>(".modal-head");
        if (header && !header.contains(active)) {
          const area = header.getBoundingClientRect();
          if (area.top < bottom && area.bottom > top)
            top = Math.max(top, area.bottom);
        }
        // Account for a sticky action row without tying Modal to a module's CSS.
        for (const actions of dialog.querySelectorAll<HTMLElement>(
          "form > :last-child, .modal-actions",
        )) {
          const style = getComputedStyle(actions);
          if (
            actions.contains(active) ||
            style.position !== "sticky" ||
            style.bottom === "auto"
          )
            continue;
          const area = actions.getBoundingClientRect();
          if (area.top < bottom && area.bottom > top)
            bottom = Math.min(bottom, area.top);
        }
        return { top, bottom };
      };
      const clipped = () => {
        const area = active.getBoundingClientRect();
        const visible = bounds();
        return (
          visible.bottom > visible.top &&
          (area.top < visible.top || area.bottom > visible.bottom)
        );
      };
      if (!clipped()) return;
      active.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "instant",
      });
      // Some mobile browsers resize only the visual viewport. Native scrolling
      // may still use the larger layout viewport, so correct its remaining clip.
      for (const scroller of scrollers) {
        if (!clipped()) break;
        const area = active.getBoundingClientRect();
        const visible = bounds();
        const delta =
          area.top < visible.top
            ? area.top - visible.top
            : area.bottom - visible.bottom;
        scroller.scrollBy({ top: delta, behavior: "instant" });
      }
    };
    const requestReveal = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(revealFocusedField);
    };
    window.addEventListener("resize", requestReveal);
    viewport?.addEventListener("resize", requestReveal);
    return () => {
      window.removeEventListener("resize", requestReveal);
      viewport?.removeEventListener("resize", requestReveal);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={`modal${wide ? " wide" : ""}${mobileFullScreen ? " mobile-full-screen" : ""} ${className}`.trim()}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-head">
        <h2 id={titleId}>{title}</h2>
        <Button
          aria-label="Fechar"
          icon="close"
          kind="ghost"
          onClick={onClose}
        />
      </div>
      {dialogError && (
        <div className="dialog-error" ref={errorRef} tabIndex={-1}>
          <Banner kind="danger">{dialogError}</Banner>
        </div>
      )}
      {children}
    </dialog>
  );
}
export function tabKeys(e: React.KeyboardEvent<HTMLElement>) {
  if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
  const tabs = Array.from(
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
  );
  const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
  const next =
    e.key === "Home"
      ? 0
      : e.key === "End"
        ? tabs.length - 1
        : (current + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) %
          tabs.length;
  if (tabs[next]) {
    e.preventDefault();
    tabs[next].focus();
    tabs[next].click();
  }
}
export const permissionLabels: Record<Permission, string> = {
  access: "Acessar ofícios",
  create: "Criar rascunhos",
  edit: "Editar documentos",
  generate: "Usar redação com IA",
  share: "Compartilhar",
  finalize: "Finalizar e numerar",
  protocol: "Protocolar no Solar",
};
export function setCSRF(value: string) {
  csrf = value;
}
